import { format, parse, parseISO } from 'date-fns';

import { REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP_1 } from 'constants/ProjectConstants';

import { EngagementContent } from '../types/engagement';
import { GoalsCheckinPendingContent } from '../types/goalsCheckingPending';
import { InformationalContent } from '../types/informational';
import { NudgeCard, NudgeData, UITemplate } from '../types/nudgeCard';
import { NudgeContentProps } from '../types/nudgeContentProps';
import { KnowYourTeamContent } from '../types/oneOnOne';
import { OverviewExplanation } from '../types/overviewExplanation';
import { RequestedFeedbackContent } from '../types/requestedFeedback';
import { ReviewPendingContent } from '../types/reviewPending';
import { SharePraiseContent } from '../types/sharePraise';

const getButtonLink = (buttonLink: string) => {
  if (buttonLink.startsWith('http')) {
    return buttonLink;
  }
  return `${window.location.origin}${buttonLink}`;
};

const nudgeContentMap: Record<UITemplate, (data: NudgeData['nudgeContent']) => NudgeContentProps[]> = {
  REVIEW: (data: ReviewPendingContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      employeeName: nudgeContent.displayName,
      employeeProfilePhoto: nudgeContent.profilePhoto,
      reviewCycleName: nudgeContent.reviewCycleName,
      reviewType: `${REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP_1[nudgeContent.providerType]} review`,
      dueBy: nudgeContent.endDate ? format(parse(nudgeContent.endDate, 'dd-MM-yyyy', new Date()), 'MMM dd, yyyy') : '',
      buttonText: nudgeContent.buttonText,
      buttonLink: getButtonLink(nudgeContent.buttonLink),
    })),
  GOAL_CHECKIN_PENDING: (data: GoalsCheckinPendingContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      title: nudgeContent.text,
      achieved: nudgeContent.actual,
      status: nudgeContent.goalProgressLevel,
      buttonText: nudgeContent.buttonText,
      buttonLink: nudgeContent.buttonLink,
      explanation: nudgeContent?.explanation,
      target: nudgeContent.target,
      startingValue: nudgeContent.startingValue,
      actual: nudgeContent.actual,
    })),
  SHARE_PRAISE: (data: SharePraiseContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      employeeName: nudgeContent.displayName,
      employeeProfilePhoto: nudgeContent.profilePhoto,
      designation: nudgeContent.designation,
      buttonText: nudgeContent.buttonText,
      buttonLink: nudgeContent.buttonLink,
      explanation: nudgeContent?.explanation,
    })),
  SHARE_ADVICE: (data: SharePraiseContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      employeeName: nudgeContent.displayName,
      employeeProfilePhoto: nudgeContent.profilePhoto,
      designation: nudgeContent.designation,
      buttonText: nudgeContent.buttonText,
      buttonLink: nudgeContent.buttonLink,
    })),
  FEEDBACK: (data: SharePraiseContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      employeeName: nudgeContent.displayName,
      employeeProfilePhoto: nudgeContent.profilePhoto,
      designation: nudgeContent.designation,
      buttonText: nudgeContent.buttonText,
      buttonLink: nudgeContent.buttonLink,
    })),
  REQUESTED_FEEDBACK: (data: RequestedFeedbackContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      employeeName: nudgeContent.displayName,
      employeeProfilePhoto: nudgeContent.profilePhoto,
      designation: nudgeContent.designation,
      requestReason: nudgeContent.questionText,
      buttonText: nudgeContent.buttonText,
      buttonLink: nudgeContent.buttonLink,
    })),
  KNOW_YOUR_TEAM: (data: KnowYourTeamContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      employeeName: nudgeContent.displayName,
      employeeProfilePhoto: nudgeContent.profilePhoto,
      scheduleDate: new Date(Number(nudgeContent.startTimeTs)),
      duration: Number(nudgeContent.duration),
      buttonText: nudgeContent.buttonText,
      buttonLink: getButtonLink(nudgeContent.buttonLink),
      explanation: nudgeContent?.explanation,
    })),
  ENGAGEMENT: (data: EngagementContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      surveyName: nudgeContent.name,
      date: parseISO(nudgeContent.deadline),
      buttonText: nudgeContent.buttonText,
      buttonLink: getButtonLink(nudgeContent.buttonLink),
    })),
  INFORMATIONAL: (data: InformationalContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      imgBig: nudgeContent.imgBig,
      imgSmall: nudgeContent.imgSmall,
      imgClickLink: getButtonLink(nudgeContent.imgClickLink),
    })),
  OVERVIEW_EXPLANATION: (data: OverviewExplanation[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      actionItemData: nudgeContent.actionItemData,
      explanation: nudgeContent.explanation,
      buttonText: nudgeContent.buttonText,
      buttonLink: nudgeContent.buttonLink,
    })),
  TEAM_PROMOTION_ACKNOWLEDGE: (data: SharePraiseContent[]) =>
    data.map((nudgeContent) => ({
      nudgeId: nudgeContent.nudgeId,
      buttonText: nudgeContent.buttonText,
    })),
};

export const mapNudgeDataToProps = (nudgeCards: NudgeCard[]) => {
  if (!Array.isArray(nudgeCards)) {
    return [];
  }

  return nudgeCards.map((nudgeCard) => ({
    ...nudgeCard,
    data: {
      ...nudgeCard.data,
      nudgeContent: nudgeContentMap[nudgeCard.uiTemplate]?.(nudgeCard.data.nudgeContent) ?? [],
    },
  }));
};
